// Arcade taxi physics. Deterministic, engine-independent, runs in the browser, in bots and in the
// headless AI player. Units: metres, seconds, radians. Babylon axes: forward = (sin yaw, 0, cos yaw).

import { Ground } from './ground';
import { circleVsObstacle } from './collide';

export interface CarInput {
  /** -1 left .. 1 right */
  steer: number;
  /** 0..1, brakes then reverses when held while stopped */
  brake: number;
  nitro: boolean;
  /** 0..1 accelerator; 0 coasts (engine braking) */
  throttle: number;
}

export interface CarTuning {
  maxSpeed: number;      // m/s forward
  reverseSpeed: number;
  accel: number;         // m/s^2 at zero speed
  brakeDecel: number;
  drag: number;          // per second fraction
  steerRate: number;     // rad/s at full lock
  grip: number;          // lateral velocity kill per second
  radius: number;        // collision circle radius (two of them, front and rear, make a capsule)
  halfLength: number;    // distance from the centre to each circle centre
  restitution: number;
  nitroBoost: number;    // multiplier on maxSpeed and accel
  offRoadFactor: number; // maxSpeed multiplier on grass/sand
}

export const DEFAULT_TUNING: CarTuning = {
  maxSpeed: 44,
  reverseSpeed: 9,
  accel: 16,
  brakeDecel: 24,
  drag: 0.35,
  steerRate: 2.2,
  grip: 9,
  radius: 1.0,
  halfLength: 1.35,
  restitution: 0.35,
  nitroBoost: 1.45,
  offRoadFactor: 0.6,
};

/** Contact on the car in its local frame (+z forward); normal points out of the struck panel. */
export interface ImpactContact { x: number; z: number; nx: number; nz: number; }
export interface CarState {
  x: number; y: number; z: number;
  yaw: number;
  /** world velocity */
  vx: number; vy: number; vz: number;
  /** signed forward speed (m/s), derived each step for convenience */
  speed: number;
  airborne: boolean;
  /** visual: front wheel angle, wheel spin accumulator */
  wheelAngle: number;
  wheelSpin: number;
  /** most recent collision impact speed (m/s) this step, 0 if none */
  impact: number;
  impactKind: 'none' | 'world' | 'car' | 'wall';
  impactContact?: ImpactContact | null;
}

export function newCarState(x: number, y: number, z: number, yaw: number): CarState {
  return { x, y, z, yaw, vx: 0, vy: 0, vz: 0, speed: 0, airborne: false, wheelAngle: 0, wheelSpin: 0, impact: 0, impactKind: 'none', impactContact: null };
}

export interface WorldBounds { x: [number, number]; z: [number, number]; }

const GRAVITY = -20;

function recordImpact(s: CarState, speed: number, kind: CarState['impactKind'], nx: number, nz: number, off: number, radius: number): void {
  if (speed <= s.impact) return;
  s.impact = speed; s.impactKind = kind;
  const right = -(nx * Math.cos(s.yaw) - nz * Math.sin(s.yaw));
  const forward = -(nx * Math.sin(s.yaw) + nz * Math.cos(s.yaw));
  s.impactContact = { x: right * radius, z: off + forward * radius, nx: right, nz: forward };
}

/** Push the car out along (nx,nz) by depth, reflect the velocity, and turn it a little when a corner hit. */
function resolve(s: CarState, nx: number, nz: number, depth: number, off: number, tune: CarTuning): void {
  s.x += nx * depth; s.z += nz * depth;
  const vn = s.vx * nx + s.vz * nz;
  if (vn < 0) {
    recordImpact(s, -vn, 'world', nx, nz, off, tune.radius);
    s.vx -= (1 + tune.restitution) * vn * nx;
    s.vz -= (1 + tune.restitution) * vn * nz;
    // a hit on the front or rear corner rotates the car away from the wall
    if (off) { const rx = Math.cos(s.yaw), rz = -Math.sin(s.yaw); const side = nx * rx + nz * rz; s.yaw += Math.sign(off) * side * Math.min(0.12, -vn * 0.01); }
  }
}

/** Advance one car by dt. Other cars are passed as simple circles for car-car bumps. */
export function stepCar(
  s: CarState, input: CarInput, tune: CarTuning, ground: Ground, bounds: WorldBounds, dt: number,
  others: { x: number; z: number; r: number; vx: number; vz: number }[] = [],
): void {
  s.impact = 0; s.impactKind = 'none'; s.impactContact = null;
  const fx = Math.sin(s.yaw), fz = Math.cos(s.yaw);
  const rx = fz, rz = -fx; // right vector
  let forward = s.vx * fx + s.vz * fz;
  let lateral = s.vx * rx + s.vz * rz;

  const surface = ground.surfaceAt(s.x, s.z);
  const onRoad = surface === 2 || surface === 4 || surface === 3 || surface === 5;
  const boost = input.nitro ? tune.nitroBoost : 1;
  const maxSpeed = tune.maxSpeed * boost * (onRoad ? 1 : tune.offRoadFactor);

  if (!s.airborne) {
    if (input.brake > 0.05) {
      if (forward > 0.3) forward = Math.max(0, forward - tune.brakeDecel * input.brake * dt);
      else forward = Math.max(-tune.reverseSpeed, forward - tune.accel * 0.6 * input.brake * dt);
    } else if (input.throttle > 0.05) {
      const a = tune.accel * boost * input.throttle * Math.max(0.15, 1 - Math.max(0, forward) / maxSpeed);
      forward = forward < 0 ? Math.min(0, forward + tune.brakeDecel * dt) : Math.min(maxSpeed, forward + a * dt);
    } else {
      // coasting: engine braking brings the car gently to a stop
      forward = forward > 0 ? Math.max(0, forward - 3 * dt) : Math.min(0, forward + 3 * dt);
    }
    forward -= forward * tune.drag * dt;
    if (forward > maxSpeed) forward = Math.max(maxSpeed, forward - tune.brakeDecel * 0.5 * dt);
    // steering: less authority when crawling, a little less at very high speed
    const speedFactor = Math.min(1, Math.abs(forward) / 6) * (1 - 0.25 * Math.min(1, Math.abs(forward) / 30));
    const steer = Math.max(-1, Math.min(1, input.steer));
    s.yaw += steer * tune.steerRate * speedFactor * dt * (forward < 0 ? -1 : 1);
    s.wheelAngle += (steer * 0.55 - s.wheelAngle) * Math.min(1, 12 * dt);
    // grip kills sideways velocity
    lateral -= lateral * Math.min(1, tune.grip * dt);
  } else {
    s.wheelAngle *= 1 - Math.min(1, 6 * dt);
  }

  // recompose world velocity after yaw change
  const nfx = Math.sin(s.yaw), nfz = Math.cos(s.yaw);
  const nrx = nfz, nrz = -nfx;
  s.vx = nfx * forward + nrx * lateral;
  s.vz = nfz * forward + nrz * lateral;

  // integrate horizontal
  s.x += s.vx * dt;
  s.z += s.vz * dt;

  // vertical: follow the ground, fly off ramps
  const g = ground.heightAt(s.x, s.z);
  const groundY = g < -0.5 ? -0.6 : g; // water/no data: sit just below sea level so we can detect it
  if (s.airborne) {
    s.vy += GRAVITY * dt;
    s.y += s.vy * dt;
    if (s.y <= groundY) { s.y = groundY; s.airborne = false; if (s.vy < -6) { s.impact = Math.max(s.impact, -s.vy * 0.4); s.impactKind = 'world'; } s.vy = 0; }
  } else {
    const dy = groundY - s.y;
    if (dy < -0.6 && Math.hypot(s.vx, s.vz) > 8) {
      // ground fell away fast: launch
      s.airborne = true; s.vy = 0;
    } else {
      s.y += dy * Math.min(1, 14 * dt);
      // climbing a slope costs speed; descending gains a bit
      if (Math.abs(dy) > 0.01) {
        const slopeLoss = Math.max(-0.5, Math.min(0.5, dy)) * 0.6;
        s.vx -= s.vx * slopeLoss * dt * 4; s.vz -= s.vz * slopeLoss * dt * 4;
      }
    }
  }

  // collide a capsule (front and rear circles) against the occupancy grid and thin obstacles
  const r = tune.radius;
  const hl = tune.halfLength ?? 0;
  for (const off of hl ? [hl, -hl] : [0]) {
    const cfx = Math.sin(s.yaw), cfz = Math.cos(s.yaw);
    const px = s.x + cfx * off, pz = s.z + cfz * off;
    // solid cells of the occupancy grid (walls, pipes, barriers), each an axis-aligned square
    const g = ground; const bc = g.bcell;
    const c0 = Math.floor((px - r - g.origin[0]) / bc), c1 = Math.floor((px + r - g.origin[0]) / bc);
    const r0 = Math.floor((pz - r - g.origin[1]) / bc), r1 = Math.floor((pz + r - g.origin[1]) / bc);
    for (let rr = Math.max(0, r0); rr <= Math.min(g.brows - 1, r1); rr++) for (let cc = Math.max(0, c0); cc <= Math.min(g.bcols - 1, c1); cc++) {
      const i = rr * g.bcols + cc;
      if (!g.blocked[i]) continue;
      if (s.airborne && s.y > groundY + g.tops[i] / 2) continue; // jumped over it
      const cx = g.origin[0] + (cc + 0.5) * bc, cz = g.origin[1] + (rr + 0.5) * bc;
      const dx = px - cx, dz = pz - cz; const hx = bc / 2, hz = bc / 2;
      const qx = Math.max(-hx, Math.min(hx, dx)), qz = Math.max(-hz, Math.min(hz, dz));
      let nx = dx - qx, nz = dz - qz; let d = Math.hypot(nx, nz);
      if (d >= r) continue;
      if (d < 1e-6) { const pxd = hx - Math.abs(dx), pzd = hz - Math.abs(dz); if (pxd < pzd) { nx = Math.sign(dx) || 1; nz = 0; d = -pxd; } else { nx = 0; nz = Math.sign(dz) || 1; d = -pzd; } }
      else { nx /= d; nz /= d; }
      resolve(s, nx, nz, r - d, off, tune);
    }
    // thin obstacles (trunks, poles) as circles
    for (const o of ground.nearby(px, pz, r + 0.5)) {
      if (o.p[1] > s.y + 1.2 || o.top < s.y + 0.2) continue;
      const hit = circleVsObstacle(px, pz, r, o);
      if (hit) resolve(s, hit.nx, hit.nz, hit.depth, off, tune);
    }
  }

  // other cars (circle-circle around the body centres, symmetric push handled by each side on its own car)
  for (const o of others) {
    const dx = s.x - o.x, dz = s.z - o.z; const d = Math.hypot(dx, dz); const rr = r + hl * 0.5 + o.r;
    if (d >= rr || d < 1e-6) continue;
    const nx = dx / d, nz = dz / d; const depth = rr - d;
    s.x += nx * depth * 0.5; s.z += nz * depth * 0.5;
    const rvx = s.vx - o.vx, rvz = s.vz - o.vz; const vn = rvx * nx + rvz * nz;
    if (vn < 0) {
      recordImpact(s, -vn * 0.5, 'car', nx, nz, 0, r);
      s.vx -= (1 + tune.restitution) * vn * nx * 0.5;
      s.vz -= (1 + tune.restitution) * vn * nz * 0.5;
    }
  }

  // boundary walls (invisible, just inside the play area)
  const wall = (pos: number, lo: number, hi: number, axis: 'x' | 'z') => {
    if (pos - r < lo) { const vn = axis === 'x' ? s.vx : s.vz; if (vn < 0) recordImpact(s, -vn, 'wall', axis === 'x' ? 1 : 0, axis === 'z' ? 1 : 0, 0, r); if (axis === 'x') { s.x = lo + r; s.vx = Math.abs(s.vx) * tune.restitution; } else { s.z = lo + r; s.vz = Math.abs(s.vz) * tune.restitution; } }
    if (pos + r > hi) { const vn = axis === 'x' ? s.vx : s.vz; if (vn > 0) recordImpact(s, vn, 'wall', axis === 'x' ? -1 : 0, axis === 'z' ? -1 : 0, 0, r); if (axis === 'x') { s.x = hi - r; s.vx = -Math.abs(s.vx) * tune.restitution; } else { s.z = hi - r; s.vz = -Math.abs(s.vz) * tune.restitution; } }
  };
  wall(s.x, bounds.x[0], bounds.x[1], 'x');
  wall(s.z, bounds.z[0], bounds.z[1], 'z');

  s.speed = s.vx * Math.sin(s.yaw) + s.vz * Math.cos(s.yaw);
  s.wheelSpin += s.speed * dt / 0.35;
}
